import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {
    MeshCellBorder,
    MeshCellBorderSide,
    MeshCellBorderType
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellBorder';
import {MeshCellFontName} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellFont';
import {
    MeshCellTextAlign, MeshCellTextWrapping,
    MeshCellVertAlign
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellTextAlign';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {ISheetCellSettings} from '@mintdata/mint-kit/api/functions/sheet/SheetCellSettings';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export type MeshCellSettingsId = string;

export type MeshCellColor = string;

export enum MeshCellSettingsProps
{
    BgColor = 'bgColor',
    TextColor = 'textColor',

    TextBold = 'textBold',
    TextItalic = 'textItalic',
    TextUnderline = 'textUnderline',
    TextStrikethrough = 'textStrikethrough',

    FontName = 'fontName',
    FontSize = 'fontSize',

    TextAlign = 'textAlign',
    VertAlign = 'vertAlign',
    WrapText = 'wrapText',

    BorderTop = 'borderTop',
    BorderLeft = 'borderLeft',
    BorderRight = 'borderRight',
    BorderBottom = 'borderBottom',

    IsValueMasked = 'isValueMasked',

    Timestamp = 'timestamp',
}

export interface IMeshCellSettings extends ISheetCellSettings
{
    [MeshCellSettingsProps.BgColor]:MeshCellColor;
    [MeshCellSettingsProps.TextColor]:MeshCellColor;

    [MeshCellSettingsProps.TextBold]:boolean;
    [MeshCellSettingsProps.TextItalic]:boolean;
    [MeshCellSettingsProps.TextUnderline]:boolean;
    [MeshCellSettingsProps.TextStrikethrough]:boolean;

    [MeshCellSettingsProps.FontName]:MeshCellFontName;
    [MeshCellSettingsProps.FontSize]:number;

    [MeshCellSettingsProps.TextAlign]:MeshCellTextAlign;
    [MeshCellSettingsProps.VertAlign]:MeshCellVertAlign;
    [MeshCellSettingsProps.WrapText]:MeshCellTextWrapping;

    [MeshCellSettingsProps.BorderTop]:MeshCellBorder;
    [MeshCellSettingsProps.BorderLeft]:MeshCellBorder;
    [MeshCellSettingsProps.BorderRight]:MeshCellBorder;
    [MeshCellSettingsProps.BorderBottom]:MeshCellBorder;

    [MeshCellSettingsProps.IsValueMasked]:boolean;
    [MeshCellSettingsProps.Timestamp]:number;
}

export const DefaultMeshCellSettings:Partial<IMeshCellSettings> =
    {
        [MeshCellSettingsProps.BgColor]: '#ffffff',
        [MeshCellSettingsProps.TextColor]: '#4c4c4c',

        [MeshCellSettingsProps.FontName]: MeshCellFontName.Arial,
        [MeshCellSettingsProps.FontSize]: 10,
        fontSizeMax: 400, // Google
        fontSizeMin: 6, // Google

        gridColor: 'rgba(0,0,0,.15)',
        borderColor: '#000000'
    };

export enum CellSettingsSortOrder
{
    ASC = 1,
    DESC = -1
}

export interface IOrderedMeshCellSettings
{
    location:MeshLocation;
    cellSettings:MeshCellSettings;
}

export interface IMeshCellSettingsSnapshot extends IMeshCellSettings
{
    id:MeshCellSettingsId;
}

export class MeshCellSettings implements IMeshCellSettings
{
    public id:MeshCellSettingsId;

    @observable
    public bgColor:MeshCellColor;

    @observable
    public textColor:MeshCellColor;

    @observable
    public textBold:boolean;

    @observable
    public textItalic:boolean;

    @observable
    public textUnderline:boolean;

    @observable
    public textStrikethrough:boolean;

    @observable
    public fontName:MeshCellFontName;

    @observable
    public fontSize:number;

    @observable
    public textAlign:MeshCellTextAlign;

    @observable
    public vertAlign:MeshCellVertAlign;

    @observable
    public wrapText:MeshCellTextWrapping;

    @observable
    public borderTop:MeshCellBorder;

    @observable
    public borderLeft:MeshCellBorder;

    @observable
    public borderRight:MeshCellBorder;

    @observable
    public borderBottom:MeshCellBorder;

    @observable
    public isValueMasked:boolean;

    @observable
    public timestamp:number;

    private constructor()
    {
    }

    public static create():MeshCellSettings
    {
        const meshCellSettings:MeshCellSettings = new MeshCellSettings();

        meshCellSettings.id = IdUtil.newGUID();

        return meshCellSettings;
    }

    public getProperty<T extends keyof IMeshCellSettings>(prop:T):IMeshCellSettings[T]
    {
        return (this as any)[prop];
    }

    public clone():MeshCellSettings
    {
        const meshCellSettings:MeshCellSettings = MeshCellSettings.create();

        meshCellSettings.bgColor = this.bgColor;
        meshCellSettings.textColor = this.textColor;
        meshCellSettings.textBold = this.textBold;
        meshCellSettings.textItalic = this.textItalic;
        meshCellSettings.textUnderline = this.textUnderline;
        meshCellSettings.textStrikethrough = this.textStrikethrough;
        meshCellSettings.fontName = this.fontName;
        meshCellSettings.fontSize = this.fontSize;
        meshCellSettings.textAlign = this.textAlign;
        meshCellSettings.vertAlign = this.vertAlign;
        meshCellSettings.wrapText = this.wrapText;
        meshCellSettings.borderTop = this.borderTop;
        meshCellSettings.borderLeft = this.borderLeft;
        meshCellSettings.borderRight = this.borderRight;
        meshCellSettings.borderBottom = this.borderBottom;
        meshCellSettings.isValueMasked = this.isValueMasked;
        meshCellSettings.timestamp = this.timestamp;

        return meshCellSettings;
    }

    @action
    public setProperty<T extends keyof IMeshCellSettings>(prop:T, value:IMeshCellSettings[T]):void
    {
        (this as any)[prop] = value;
    }

    @action
    public setBorder(side:MeshCellBorderSide, type:MeshCellBorderType, color?:MeshCellColor):void
    {
        const borderSide = `border${side}`;

        (this as any)[borderSide] = type === MeshCellBorderType.Grid
            ? null
            : new MeshCellBorder(type, color);
    }

    @action
    public setIsValueMasked(value:boolean):void
    {
        this.isValueMasked = value;
    }

    @action
    public toggleMaskedValue():void
    {
        this.isValueMasked = !this.isValueMasked;
    }

    public static toJson:ITransformer<MeshCellSettings, IMeshCellSettingsSnapshot> = createTransformer((meshCellSettings:MeshCellSettings) =>
    {
        const snapshot:IMeshCellSettingsSnapshot = {
            id: meshCellSettings.id,
            bgColor: meshCellSettings.bgColor,
            textColor: meshCellSettings.textColor,
            textBold: meshCellSettings.textBold,
            textItalic: meshCellSettings.textItalic,
            textUnderline: meshCellSettings.textUnderline,
            textStrikethrough: meshCellSettings.textStrikethrough,
            fontName: meshCellSettings.fontName,
            fontSize: meshCellSettings.fontSize,
            textAlign: meshCellSettings.textAlign,
            vertAlign: meshCellSettings.vertAlign,
            wrapText: meshCellSettings.wrapText,
            borderTop: meshCellSettings.borderTop,
            borderLeft: meshCellSettings.borderLeft,
            borderRight: meshCellSettings.borderRight,
            borderBottom: meshCellSettings.borderBottom,
            isValueMasked: meshCellSettings.isValueMasked,
            timestamp: meshCellSettings.timestamp
        };

        return Object.entries(snapshot).reduce((json, [key, value]) =>
        {
            if( value !== null && value !== undefined )
            {
                json[key] = value;
            }

            return json;
        }, {} as IMeshCellSettingsSnapshot);
    });

    public static fromJson(json:IMeshCellSettingsSnapshot,
                           currentState?:MeshCellSettings,
                           currentJson?:IMeshCellSettingsSnapshot,
                           ctx?:DesignStateFromJsonCtx):MeshCellSettings
    {
        return MeshCellSettings.fromJsonDeep(json);
    }

    public static fromJsonDeep(json:IMeshCellSettingsSnapshot):MeshCellSettings
    {
        const meshCellSettings:MeshCellSettings = new MeshCellSettings();

        meshCellSettings.id = json.id;
        meshCellSettings.bgColor = json.bgColor;
        meshCellSettings.textColor = json.textColor;
        meshCellSettings.textBold = json.textBold;
        meshCellSettings.textItalic = json.textItalic;
        meshCellSettings.textUnderline = json.textUnderline;
        meshCellSettings.textStrikethrough = json.textStrikethrough;
        meshCellSettings.fontName = json.fontName;
        meshCellSettings.fontSize = json.fontSize;
        meshCellSettings.textAlign = json.textAlign;
        meshCellSettings.vertAlign = json.vertAlign;
        meshCellSettings.wrapText = json.wrapText;
        meshCellSettings.borderTop = json.borderTop;
        meshCellSettings.borderLeft = json.borderLeft;
        meshCellSettings.borderRight = json.borderRight;
        meshCellSettings.borderBottom = json.borderBottom;
        meshCellSettings.isValueMasked = json.isValueMasked;
        meshCellSettings.timestamp = json.timestamp;

        return meshCellSettings;
    }
}
